import { StyleSheet, Pressable, Platform, Animated } from "react-native";
import { PressableScale } from "pressto";
import { Modal } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Text, View } from "@src/components";
import { Button } from "@src/components/Button";
import { sizes } from "@src/constants/sizes";
import { UnistylesRuntime } from "react-native-unistyles";
import { presentLegacyAppWithCredentials } from "@src/features/auth/utils/legacy-kit-auth";
import { useState, useEffect, useRef } from "react";
import { Icon } from "../Icon";

type HolidayReminderModalProps = {
  visible: boolean;
  onClose: () => void;
  onDismiss?: () => void;
  onHireCamera?: () => Promise<void> | void;
  onRemindLater?: () => void;
  hireButtonLoading?: boolean;
};

export function HolidayReminderModal({
  visible,
  onClose,
  onDismiss,
  onHireCamera,
  onRemindLater,
  hireButtonLoading = false,
}: HolidayReminderModalProps) {
  const [showToast, setShowToast] = useState(false);
  const toastOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (showToast) {
      Animated.timing(toastOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();

      const timer = setTimeout(() => {
        Animated.timing(toastOpacity, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }).start(() => {
          setShowToast(false);
          // if (Platform.OS === "android") {
          onClose();
          // }
          presentLegacyAppWithCredentials();
        });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showToast, toastOpacity, onClose]);

  const handleHireCamera = async () => {
    if (onHireCamera) {
      await onHireCamera();
    }
    setShowToast(true);
  };

  const handleRemindLater = () => {
    // if (Platform.OS === "android") {
    onClose();
    // }
    onRemindLater?.();
    presentLegacyAppWithCredentials();
  };

  const handleDismiss = () => {
    onDismiss?.();
    // if (Platform.OS === "android") {
    onClose();
    // }
    presentLegacyAppWithCredentials();
  };

  const theme = UnistylesRuntime.getTheme();
  const isDarkMode = UnistylesRuntime.themeName === "dark";

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => {
        onClose();
      }}
      presentationStyle="overFullScreen"
      statusBarTranslucent={Platform.OS === "android"}
      hardwareAccelerated
    >
      {/* Semi-transparent dark overlay */}
      <Pressable style={styles.overlay} onPress={handleDismiss}>
        <SafeAreaView style={styles.safeArea}>
          <Pressable onPress={(e) => e.stopPropagation()}>
            <View bg="background" borderRadius={sizes.md} padding="6xl">
              {/* Header with close button */}
              <View style={styles.header}>
                <View style={{ flex: 1 }} />
                <PressableScale
                  onPress={handleDismiss}
                  style={styles.closeButton}
                  hitSlop={10}
                  accessibilityLabel="Close modal"
                  accessibilityRole="button"
                >
                  <Icon
                    name="close"
                    size={24}
                    color={isDarkMode ? theme.colors.white.default : theme.colors.navy[700]}
                  />
                </PressableScale>
              </View>

              {/* Title */}
              <Text
                textStyle="h2"
                fontWeight="normal"
                mb="3xl"
                style={{
                  color: isDarkMode ? theme.colors.white.default : theme.colors.navy[600],
                }}
              >
                Holiday reminder
              </Text>

              {/* Message */}
              <Text
                textStyle="lg/regular"
                mb="6xl"
                lineHeight={28}
                style={{
                  color: isDarkMode ? theme.colors.white.default : theme.colors.navy[600],
                  marginRight: sizes.md,
                }}
              >
                This is our busiest period, don&apos;t miss out on protecting your site and assets.
              </Text>

              {/* Buttons */}
              <View style={styles.buttonContainer}>
                <Button
                  label="Hire a camera"
                  size="lg"
                  variant="primary"
                  variantColor="blue"
                  onPress={handleHireCamera}
                  loading={hireButtonLoading}
                  textStyle="md/medium"
                />
                <Button
                  label="Remind me later"
                  size="lg"
                  variant="secondary"
                  variantColor="grey"
                  onPress={handleRemindLater}
                  textColor={isDarkMode ? theme.colors.white.default : theme.colors.blue[500]}
                  style={{ borderColor: isDarkMode ? theme.colors.navy[100] : theme.colors.blue[500] }}
                  textStyle="md/medium"
                />
              </View>

              {/* Disclaimer */}
              <Text
                textStyle="xs/regular"
                mb="3xl"
                style={[
                  styles.disclaimer,
                  {
                    color: isDarkMode ? theme.colors.navy[100] : theme.colors.navy[600],
                  },
                ]}
              >
                *Remind me later will send follow-up reminder
              </Text>
            </View>
          </Pressable>
        </SafeAreaView>

        {/* Success Toast - positioned at bottom of overlay */}
        {showToast && (
          <Animated.View
            style={[
              styles.toast,
              {
                opacity: toastOpacity,
                backgroundColor: theme.colors.success[500],
              },
            ]}
          >
            <Icon name="checkmarkOutline" size={20} color="white" />
            <Text textStyle="sm/medium" style={{ color: "white", marginLeft: sizes.xs }}>
              Thanks! We&apos;ll reach out shortly
            </Text>
          </Animated.View>
        )}
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
    elevation: 999,
    zIndex: 999,
  },
  safeArea: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    paddingHorizontal: sizes.md,
  },
  modalContainer: {
    borderRadius: sizes.md,
    width: "100%",
    maxWidth: 400,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
  },
  closeButton: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  title: {
    marginBottom: sizes.md,
  },
  message: {
    marginBottom: sizes.lg,
  },
  buttonContainer: {
    gap: sizes.xs,
    marginBottom: sizes.sm,
  },
  disclaimer: {
    textAlign: "center",
  },
  toast: {
    position: "absolute",
    bottom: 40,
    left: sizes.md,
    right: sizes.md,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: sizes.sm,
    paddingHorizontal: sizes.md,
    borderRadius: sizes.sm,
  },
});
